import React, { useState, useEffect } from 'react';
import { Calculator, Clock, DollarSign } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const YouTubeViewTimeValueCalculator = () => {
  const [totalViewTime, setTotalViewTime] = useState(1000000);
  const [totalRevenue, setTotalRevenue] = useState(5000);
  const [viewTimeValue, setViewTimeValue] = useState(0);

  useEffect(() => {
    calculateViewTimeValue();
  }, [totalViewTime, totalRevenue]);

  const calculateViewTimeValue = () => {
    const value = (totalRevenue / totalViewTime) * 1000;
    setViewTimeValue(parseFloat(value.toFixed(4)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/youtube-view-time-value-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>YouTube View Time Value Calculator: Monetize Watch Time | Free Tool</title>
        <meta name="description" content="Calculate your YouTube View Time Value instantly. Compare with industry benchmarks, get optimization tips, and improve video monetization." />
        <meta name="keywords" content="youtube view time calculator, watch time value, video monetization, youtube analytics, content creator metrics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free YouTube View Time Calculator: Optimize Video Revenue" />
        <meta property="og:description" content="Calculate your YouTube View Time Value instantly. Get actionable insights to improve watch time and boost revenue." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free YouTube View Time Calculator: Optimize Video Revenue" />
        <meta property="twitter:description" content="Calculate your YouTube View Time Value instantly. Get actionable insights to improve watch time and boost revenue." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "YouTube View Time Value Calculator",
            "description": "Free calculator to measure and optimize YouTube video watch time value",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate View Time Value",
              "Industry Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "YouTube View Time Value",
              "description": "The monetary value generated per thousand minutes of watch time on YouTube videos."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Content Creators, YouTubers, Video Marketers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate YouTube View Time Value",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total view time in minutes"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total revenue generated"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your value per 1000 minutes and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        YouTube View Time Value Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total View Time (minutes)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Clock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalViewTime}
                  onChange={(e) => setTotalViewTime(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalRevenue}
                  onChange={(e) => setTotalRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${viewTimeValue}
            <span className="text-xl text-gray-500 ml-2">per 1000 minutes</span>
          </div>
          <p className="text-gray-600">YouTube View Time Value</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your YouTube View Time Value is ${viewTimeValue} per 1000 minutes of view time. This means that for every 1000 minutes your videos are watched, you're generating ${viewTimeValue} in revenue.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Niche Benchmarks (per 1000 minutes)</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Gaming: $2-5</li>
          <li>Education: $4-8</li>
          <li>Tech Reviews: $5-10</li>
          <li>Entertainment: $3-6</li>
          <li>Business: $6-12</li>
          <li>Lifestyle: $4-7</li>
          <li>How-to/Tutorial: $5-9</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">View Time Optimization</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Create engaging content</li>
          <li>Optimize video length</li>
          <li>Use end screens</li>
          <li>Create playlists</li>
          <li>Analyze retention data</li>
          <li>Improve thumbnails</li>
          <li>Engage with comments</li>
          <li>Cross-promote content</li>
          <li>Optimize descriptions</li>
          <li>Use cards effectively</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Retention Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Hook viewers in first 15 seconds</li>
          <li>Create series content</li>
          <li>Use pattern interrupts</li>
          <li>Add visual interest</li>
          <li>Include calls-to-action</li>
          <li>Maintain consistent quality</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Strong hooks increase retention by 40%</li>
          <li>Playlists boost watch time by 50%</li>
          <li>End screens improve session time by 25%</li>
          <li>Quality thumbnails lift CTR by 30%</li>
          <li>Engagement boosts revenue by 20%</li>
        </ul>
      </div>
    </div>
  );
};

export default YouTubeViewTimeValueCalculator;
